import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellPlus, CheckCircle2, History, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Badge, Field, SelectField } from "@/components/common";
import { api, formatApiError } from "@/lib/api";
import { PAYMENT_STATUS, fmtDate, fmtPrice, todayStr } from "@/lib/constants";
import { frToIso } from "@/components/FrDateInput";

const FILTERS = { open: "Non réglées", a_verifier: "À vérifier", a_completer: "Règlement à compléter (reprise)", en_retard: "En retard", partielle: "Partiellement payées", en_attente: "En attente", payee: "Payées", all: "Toutes" };

const InvoiceDialog = ({ inv, onClose, onSaved }) => {
  const [f, setF] = useState(null);
  const [hist, setHist] = useState(null);
  useEffect(() => {
    if (inv) {
      setF({ issue_date: inv.issue_date, due_date: inv.due_date, amount: inv.amount, amount_manual: !!inv.amount_manual, paid_amount: inv.paid_amount || 0,
        paid_date: inv.paid_date, payment_status: inv.payment_status || "en_attente", comment: inv.comment || "" });
      setHist(null);
    }
  }, [inv]);
  if (!inv || !f) return null;
  const s = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  const save = async () => {
    try { await api.put(`/invoices/${inv.id}`, f); toast.success("Règlement mis à jour"); onSaved(); onClose(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-cyan-400/20 bg-[#0F2042] text-slate-100 sm:max-w-lg" data-testid="invoice-dialog">
        <DialogTitle>Facture {inv.number}</DialogTitle>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Date d'émission"><Input type="date" value={f.issue_date || ""} onChange={(e) => s("issue_date")(e.target.value || null)} data-testid="invoice-issue-input" /></Field>
          <Field label="Échéance"><Input type="date" value={f.due_date || ""} onChange={(e) => s("due_date")(e.target.value || null)} data-testid="invoice-due-input" /></Field>
          <Field label="Montant facturé (€)">
            <Input type="number" step="0.01" min={0} value={f.amount ?? ""} onChange={(e) => setF({ ...f, amount: e.target.value === "" ? null : Number(e.target.value), amount_manual: true })} data-testid="invoice-amount-input" />
            {f.amount_manual && <button type="button" className="mt-1 text-xs text-cyan-300" onClick={() => setF({ ...f, amount_manual: false })} data-testid="invoice-amount-auto">Revenir au montant calculé</button>}
          </Field>
          <Field label="Statut"><SelectField value={f.payment_status} onChange={s("payment_status")} options={{ ...(f.payment_status === "a_completer" ? { a_completer: "Règlement à compléter" } : {}), en_attente: "En attente de règlement", partielle: "Partiellement payée", payee: "Payée" }} testid="invoice-status-select" /></Field>
          <Field label="Montant réglé (€)"><Input type="number" step="0.01" min={0} value={f.paid_amount} onChange={(e) => s("paid_amount")(Number(e.target.value))} data-testid="invoice-paid-input" /></Field>
          <Field label="Date du règlement"><Input type="date" value={f.paid_date || ""} onChange={(e) => s("paid_date")(e.target.value || null)} data-testid="invoice-paid-date-input" /></Field>
          <Field label="Commentaire" className="sm:col-span-2"><Textarea value={f.comment} onChange={s("comment")} data-testid="invoice-comment-input" /></Field>
        </div>
        <p className="text-xs text-slate-400">« En retard » est calculé à partir de l'échéance. Aucune relance n'est envoyée au client.</p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={save} className="dt-glow" data-testid="invoice-save-button">Enregistrer</Button>
          <Button variant="ghost" onClick={() => api.get(`/invoices/${inv.id}/history`).then((r) => setHist(r.data))} data-testid="invoice-history-button"><History className="mr-1 h-4 w-4" />Historique</Button>
        </div>
        {hist && (
          <ul className="space-y-1 text-xs text-slate-300" data-testid="invoice-history">
            {hist.map((h, i) => <li key={i}>{new Date(h.at).toLocaleString("fr-FR")} · {h.action === "billing_status" ? "Statut de facturation" : h.action === "follow_up" ? "Rappel interne créé" : "Règlement modifié"}{h.user_name ? ` · ${h.user_name}` : ""}</li>)}
            {!hist.length && <li>Aucun changement.</li>}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ inv, onEdit, reload }) => {
  const markPaid = async () => {
    try { await api.put(`/invoices/${inv.id}`, { ...inv, payment_status: "payee", paid_amount: inv.amount, paid_date: todayStr() }); toast.success("Facture marquée payée"); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const followUp = async () => {
    const raw = window.prompt("Date du rappel interne (jj/mm/aaaa)", todayStr().split("-").reverse().join("/"));
    if (raw === null) return;
    const due = frToIso((raw || "").trim());
    if (!due) { toast.error("Date invalide : utilisez le format jj/mm/aaaa"); return; }
    if (!due) return;
    try { await api.post(`/invoices/${inv.id}/reminder`, { due_date: due, note: `Vérifier le règlement de la facture ${inv.number}` }); toast.success("Rappel interne créé dans « Suivis & maintenance »"); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-white/10 py-2" data-testid={`invoice-row-${inv.id}`}>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-white">{inv.number} <span className="text-xs text-slate-400">émise {fmtDate(inv.issue_date)}{inv.due_date ? ` · échéance ${fmtDate(inv.due_date)}` : " · sans échéance"}</span></p>
        <p className="text-xs text-slate-400">{inv.interventions.map((i) => <Link key={i.id} to={`/interventions/${i.id}`} className="mr-2 text-cyan-300 hover:underline">{i.title}</Link>)}</p>
      </div>
      <Badge value={inv.effective_status} map={PAYMENT_STATUS} />
      {inv.to_check && <span className="rounded bg-amber-400/15 px-2 py-0.5 text-xs text-amber-200" data-testid={`invoice-to-check-${inv.id}`}>À vérifier</span>}
      <span className="font-mono-dt text-sm text-white">{fmtPrice(inv.amount || 0)}{inv.remaining !== inv.amount ? <span className="text-xs text-slate-400"> · reste {fmtPrice(inv.remaining)}</span> : null}</span>
      <div className="flex gap-1">
        {inv.effective_status !== "payee" && <Button size="icon" variant="ghost" title="Marquer payée" onClick={markPaid} data-testid={`invoice-mark-paid-${inv.id}`}><CheckCircle2 className="h-4 w-4" /></Button>}
        <Button size="icon" variant="ghost" title="Rappel interne" onClick={followUp} data-testid={`invoice-follow-up-${inv.id}`}><BellPlus className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" title="Modifier" onClick={() => onEdit(inv)} data-testid={`invoice-edit-${inv.id}`}><Pencil className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

export const InvoicesTab = ({ initialStatus = "open" }) => {
  const [status, setStatus] = useState(initialStatus);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/invoices", { params: { status, date_from: from, date_to: to } }).then((r) => setData(r.data)), [status, from, to]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="invoices-tab">
      <div className="flex flex-wrap gap-3">
        <SelectField value={status} onChange={setStatus} options={Object.entries(FILTERS)} testid="invoice-filter-select" className="w-full sm:w-72" />
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full sm:w-44" data-testid="invoice-date-from" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full sm:w-44" data-testid="invoice-date-to" />
      </div>
      {data && <p className="text-sm text-slate-300" data-testid="invoice-totals">{data.totals.count} facture(s) · {fmtPrice(data.totals.amount)} · reste dû {fmtPrice(data.totals.remaining)}</p>}
      {data && !data.clients.length && <p className="text-sm text-slate-400">Aucune facture. Les factures apparaissent ici dès qu'une intervention est marquée « Facturée ».</p>}
      {data?.clients.map((g) => (
        <section key={g.client_id || "none"} className="rounded-xl border border-cyan-400/15 bg-[#122549]/60 px-4 py-2" data-testid={`invoice-client-${g.client_id}`}>
          <div className="flex justify-between py-1"><p className="font-medium text-white">{g.client_name}</p><p className="font-mono-dt text-sm text-amber-200">reste {fmtPrice(g.remaining)}</p></div>
          {g.invoices.map((inv) => <Row key={inv.id} inv={inv} onEdit={setEdit} reload={load} />)}
        </section>
      ))}
      <InvoiceDialog inv={edit} onClose={() => setEdit(null)} onSaved={load} />
    </div>
  );
};
